import React, { useCallback, useMemo, useState } from "react"

import { AddMetricsToRunButton, type RerunMetricsById } from "@/metrics/rerun"
import { Button } from "@coval/ui"
import { CheckSquare, X } from "lucide-react"

import { useActiveItemAutoScroll } from "@/hooks/useActiveItemAutoScroll"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import type { RunRowLike } from "@/types/RunRow"

import {
  CUSTOM_UNASSIGNED_KEY,
  getGroupKeysForRow as getCompareByGroupKeys,
  type CompareBy,
  type CompareByDimension,
} from "../../compare-by"
import { useExplanationSimilarityReorder } from "../hooks/useExplanationSimilarityReorder"
import type { RunResultsMetricRenderAdaptersById } from "../types"
import { GroupedAggregateRow } from "./grouped-results-table/GroupedAggregateRow"
import { GroupedRawRow } from "./grouped-results-table/GroupedRawRow"
import { SimilarityThresholdSlider } from "./SimilarityThresholdSlider"

const GROUPED_BY_COPY: Partial<Record<CompareByDimension, string>> = {
  run: "run",
  agent: "agent",
  mutation: "mutation",
  persona: "persona",
  test_case: "test case",
  tag: "tag",
}

const GROUPED_RESULTS_MODE_OPTIONS = [
  { value: "avg", label: "Average" },
  { value: "median", label: "Median" },
  { value: "p95", label: "P95" },
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
] as const

type GroupedResultsMode = (typeof GROUPED_RESULTS_MODE_OPTIONS)[number]["value"]

interface GroupedResultsTableProps {
  runRows: RunRowLike[]
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  compareBy: CompareBy
  /** Optional second-level grouping. When set, expanding a primary group reveals secondary group rows; expanding a secondary row reveals raw simulation rows. */
  secondaryCompareBy?: CompareBy | null
  onRowClick: (simulationOutputId: string) => void
  metricIdFilter?: string | null
  agentsById?: Record<string, { display_name: string }> | null
  personasById?: Record<string, { display_name: string }> | null
  runsById?: Record<string, string> | null
  /** Per-metric visibility config. When provided, hides metric columns where visible === false. */
  metricViews?: Record<string, { visible: boolean }>
  /** When provided, the table renders a checkbox column so users can select sims (raw rows) or whole groups (cascade) for bulk actions like rerun. */
  selectedSimulationOutputIds?: string[]
  onSelectedSimulationOutputIdsChange?: (ids: string[]) => void
  /** Catalog used by the rerun bulk-action button. When omitted, rerun is hidden. */
  rerunMetricRecordsById?: RerunMetricsById | null
  /** When true, forces select mode on and shows the selection toolbar. Used during custom-dimension selection mode. */
  forceSelectMode?: boolean
  /** Optional content rendered in the selection toolbar before the rerun button. Used to inject "Assign to {group}" / "Unassign" actions during selection mode. */
  selectionToolbarLeading?: React.ReactNode
  /** Map from simulation_output_id → custom-dimension group id. When provided, a chip is shown on each raw row indicating its current group assignment. */
  customDimensionGroupBySimId?: Record<string, string>
  /** Map from custom-dimension group id → display name. Used by the per-row chip. */
  customDimensionGroupNameById?: Record<string, string>
  /** Map from custom-dimension group id → hex color. Used to color the per-row chip and tint the row background. */
  customDimensionGroupColorById?: Record<string, string>
}

export const GroupedResultsTable: React.FC<GroupedResultsTableProps> = ({
  runRows,
  metricRenderAdaptersById,
  compareBy,
  secondaryCompareBy = null,
  onRowClick,
  metricIdFilter = null,
  agentsById = null,
  personasById = null,
  runsById = null,
  metricViews,
  selectedSimulationOutputIds,
  onSelectedSimulationOutputIdsChange,
  rerunMetricRecordsById = null,
  forceSelectMode = false,
  selectionToolbarLeading,
  customDimensionGroupBySimId,
  customDimensionGroupNameById,
  customDimensionGroupColorById,
}) => {
  const [isSelectMode, setIsSelectMode] = useState(false)
  const selectionAvailable =
    selectedSimulationOutputIds !== undefined &&
    onSelectedSimulationOutputIdsChange !== undefined
  const selectionEnabled =
    selectionAvailable && (isSelectMode || forceSelectMode)
  const selectionSet = useMemo(
    () => new Set(selectedSimulationOutputIds ?? []),
    [selectedSimulationOutputIds],
  )
  const collectGroupSimIds = useCallback((rows: RunRowLike[]): string[] => {
    const ids: string[] = []
    for (const row of rows) {
      const id = row.simulation_output_id
      if (typeof id === "string" && id.length > 0) {
        ids.push(id)
      }
    }
    return ids
  }, [])
  const getGroupSelectionState = useCallback(
    (rows: RunRowLike[]): "all" | "none" | "partial" => {
      const ids = collectGroupSimIds(rows)
      if (ids.length === 0) {
        return "none"
      }
      let selectedCount = 0
      for (const id of ids) {
        if (selectionSet.has(id)) {
          selectedCount += 1
        }
      }
      if (selectedCount === 0) {
        return "none"
      }
      if (selectedCount === ids.length) {
        return "all"
      }
      return "partial"
    },
    [collectGroupSimIds, selectionSet],
  )
  const toggleGroupSelection = useCallback(
    (rows: RunRowLike[]) => {
      if (!onSelectedSimulationOutputIdsChange) {
        return
      }
      const ids = collectGroupSimIds(rows)
      if (ids.length === 0) {
        return
      }
      const idsSet = new Set(ids)
      const state = getGroupSelectionState(rows)
      const current = selectedSimulationOutputIds ?? []
      if (state === "all") {
        onSelectedSimulationOutputIdsChange(
          current.filter((id) => !idsSet.has(id)),
        )
        return
      }
      const next = new Set(current)
      for (const id of ids) {
        next.add(id)
      }
      onSelectedSimulationOutputIdsChange(Array.from(next))
    },
    [
      collectGroupSimIds,
      getGroupSelectionState,
      onSelectedSimulationOutputIdsChange,
      selectedSimulationOutputIds,
    ],
  )
  const toggleSimSelection = useCallback(
    (id: string) => {
      if (!onSelectedSimulationOutputIdsChange) {
        return
      }
      const current = selectedSimulationOutputIds ?? []
      if (selectionSet.has(id)) {
        onSelectedSimulationOutputIdsChange(current.filter((x) => x !== id))
        return
      }
      onSelectedSimulationOutputIdsChange([...current, id])
    },
    [
      onSelectedSimulationOutputIdsChange,
      selectedSimulationOutputIds,
      selectionSet,
    ],
  )
  const [mode, setMode] = useState<GroupedResultsMode>("avg")
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null)
  const [expandedSecondaryKey, setExpandedSecondaryKey] = useState<
    string | null
  >(null)

  // Multi-key helper: for dimension === "tag" a row with N tags appears
  // under every one of its N groups, matching the metric-card aggregation
  // path. Single-value dimensions still return a one-element list.
  const getGroupKeysForRow = useCallback(
    (row: RunRowLike): string[] => {
      return getCompareByGroupKeys(row, compareBy)
    },
    [compareBy],
  )
  const getSecondaryGroupKeysForRow = useCallback(
    (row: RunRowLike): string[] => {
      return secondaryCompareBy
        ? getCompareByGroupKeys(row, secondaryCompareBy)
        : []
    },
    [secondaryCompareBy],
  )

  const labelResolvers = useMemo(
    () => ({ agentsById, personasById, runsById }),
    [agentsById, personasById, runsById],
  )
  const metricColumns = useMemo(() => {
    return Object.entries(metricRenderAdaptersById || {})
      .filter(([_, metric]) => metric.metric_display_name != null)
      .filter(([metricKey]) => {
        if (metricIdFilter) {
          return metricKey === metricIdFilter
        }
        return true
      })
      .filter(([metricKey]) => {
        return metricViews?.[metricKey]?.visible !== false
      })
      .filter(([_, metric]) =>
        runRows.some(
          (row) =>
            row.metric_outputs?.[metric.id] !== undefined &&
            row.metric_outputs[metric.id] !== null,
        ),
      )
      .map(([metricName, metric]) => ({
        key: metric.id,
        name: metric.metric_display_name || metricName,
      }))
  }, [metricRenderAdaptersById, runRows, metricIdFilter, metricViews])

  const explanationTable = useMemo(
    () =>
      metricIdFilter != null &&
      metricIdFilter !== "" &&
      metricRenderAdaptersById?.[metricIdFilter]
        ? metricRenderAdaptersById[metricIdFilter].getExplanationTable(runRows)
        : { columns: [], getRowValues: () => ({}) as Record<string, string> },
    [metricIdFilter, metricRenderAdaptersById, runRows],
  )

  const explanationColumns = useMemo(() => {
    if (!explanationTable.columns.length) {
      return []
    }
    const hasAny = runRows.some((row) =>
      Object.values(explanationTable.getRowValues(row)).some(
        (v) => v.trim() !== "",
      ),
    )
    return hasAny ? explanationTable.columns : []
  }, [explanationTable, runRows])

  const groupEntries = useMemo(() => {
    const map = new Map<
      string,
      {
        rows: RunRowLike[]
      }
    >()

    for (const row of runRows) {
      // Fan-out: a two-tag row lives in both groups so per-group
      // aggregates match the metric-card view for the same report.
      for (const key of getGroupKeysForRow(row)) {
        const existing = map.get(key)
        if (existing) {
          existing.rows.push(row)
        } else {
          map.set(key, { rows: [row] })
        }
      }
    }

    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === CUSTOM_UNASSIGNED_KEY) {
        return 1
      }
      if (b === CUSTOM_UNASSIGNED_KEY) {
        return -1
      }
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
      })
    })
  }, [runRows, getGroupKeysForRow])

  const expandedPrimaryRows = useMemo(
    () =>
      groupEntries.find(([groupKey]) => groupKey === expandedGroupKey)?.[1]
        .rows ?? [],
    [expandedGroupKey, groupEntries],
  )

  const secondaryGroupEntries = useMemo(() => {
    if (!secondaryCompareBy || !expandedGroupKey) {
      return []
    }
    const map = new Map<string, { rows: RunRowLike[] }>()
    for (const row of expandedPrimaryRows) {
      // Same fan-out rule as the primary axis: multi-tag rows contribute
      // to every one of their tag buckets.
      for (const key of getSecondaryGroupKeysForRow(row)) {
        const existing = map.get(key)
        if (existing) {
          existing.rows.push(row)
        } else {
          map.set(key, { rows: [row] })
        }
      }
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    )
  }, [
    expandedGroupKey,
    expandedPrimaryRows,
    getSecondaryGroupKeysForRow,
    secondaryCompareBy,
  ])

  const rawRowSource = useMemo(() => {
    if (!secondaryCompareBy) {
      return expandedPrimaryRows
    }
    if (!expandedSecondaryKey) {
      return []
    }
    return (
      secondaryGroupEntries.find(([k]) => k === expandedSecondaryKey)?.[1]
        .rows ?? []
    )
  }, [
    expandedPrimaryRows,
    expandedSecondaryKey,
    secondaryCompareBy,
    secondaryGroupEntries,
  ])

  const explanationSimilarity = useExplanationSimilarityReorder({
    metricIdFilter,
    metricRenderAdaptersById,
    runRows,
  })

  const expandedRows = useMemo(() => {
    const valid = rawRowSource.filter(
      (row): row is RunRowLike & { simulation_output_id: string } =>
        typeof row.simulation_output_id === "string" &&
        row.simulation_output_id.length > 0,
    )
    return explanationSimilarity.reorder(valid)
  }, [rawRowSource, explanationSimilarity])

  const {
    handlePointerEnter,
    handlePointerLeave,
    isActiveItem,
    keyboardActiveItemId,
  } = useCollectionPageShortcuts({
    enabled: expandedRows.length > 0,
    getItemId: (row) => row.simulation_output_id,
    itemLabel: "simulation",
    items: expandedRows,
    onOpenItem: (row) => onRowClick(row.simulation_output_id),
  })

  useActiveItemAutoScroll({
    activeItemId: keyboardActiveItemId,
    selectorAttribute: "data-grouped-simulation-output-id",
  })

  if (!runRows.length || !metricColumns.length || !groupEntries.length) {
    return (
      <div className="p-6 text-sm text-coval-text-muted">
        No grouped results available for this configuration.
      </div>
    )
  }

  const renderGroupAggregateRow = ({
    groupKey,
    rows,
    groupCompareBy,
    isExpanded,
    onToggle,
    indentClass,
  }: {
    groupKey: string
    rows: RunRowLike[]
    groupCompareBy: CompareBy
    isExpanded: boolean
    onToggle: () => void
    indentClass: string
  }) => (
    <GroupedAggregateRow
      customDimensionGroupBySimId={customDimensionGroupBySimId}
      customDimensionGroupColorById={customDimensionGroupColorById}
      customDimensionGroupNameById={customDimensionGroupNameById}
      explanationColumnCount={explanationColumns.length}
      groupCompareBy={groupCompareBy}
      groupKey={groupKey}
      groupSelectionState={
        selectionEnabled ? getGroupSelectionState(rows) : "none"
      }
      indentClass={indentClass}
      isExpanded={isExpanded}
      labelResolvers={labelResolvers}
      metricColumns={metricColumns}
      metricRenderAdaptersById={metricRenderAdaptersById}
      mode={mode}
      onToggle={onToggle}
      onToggleSelection={toggleGroupSelection}
      rows={rows}
      selectionEnabled={selectionEnabled}
    />
  )

  const renderRawRow = (
    row: RunRowLike,
    rowIndex: number,
    indentClass: string,
  ) => (
    <GroupedRawRow
      key={row.simulation_output_id || `row-${rowIndex}`}
      customDimensionGroupBySimId={customDimensionGroupBySimId}
      customDimensionGroupColorById={customDimensionGroupColorById}
      customDimensionGroupNameById={customDimensionGroupNameById}
      explanationColumns={explanationColumns}
      explanationTable={explanationTable}
      indentClass={indentClass}
      isKeyboardActive={Boolean(
        row.simulation_output_id && isActiveItem(row.simulation_output_id),
      )}
      metricColumns={metricColumns}
      metricRenderAdaptersById={metricRenderAdaptersById}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onRowClick={onRowClick}
      onToggleSelection={toggleSimSelection}
      row={row}
      rowIndex={rowIndex}
      selected={Boolean(
        row.simulation_output_id && selectionSet.has(row.simulation_output_id),
      )}
      selectionEnabled={selectionEnabled}
    />
  )

  const renderPrimaryGroup = ({
    groupKey,
    group,
  }: {
    groupKey: string
    group: { rows: RunRowLike[] }
  }) => {
    const isExpanded = expandedGroupKey === groupKey
    return (
      <React.Fragment key={groupKey}>
        {renderGroupAggregateRow({
          groupKey,
          rows: group.rows,
          groupCompareBy: compareBy,
          isExpanded,
          onToggle: () => {
            setExpandedGroupKey((prev) => (prev === groupKey ? null : groupKey))
            setExpandedSecondaryKey(null)
          },
          indentClass: "pl-6",
        })}
        {isExpanded && secondaryCompareBy
          ? secondaryGroupEntries.map(([secKey, secGroup]) => {
              const isSecExpanded = expandedSecondaryKey === secKey
              return (
                <React.Fragment key={`${groupKey}::${secKey}`}>
                  {renderGroupAggregateRow({
                    groupKey: secKey,
                    rows: secGroup.rows,
                    groupCompareBy: secondaryCompareBy,
                    isExpanded: isSecExpanded,
                    onToggle: () =>
                      setExpandedSecondaryKey((prev) =>
                        prev === secKey ? null : secKey,
                      ),
                    indentClass: "pl-10",
                  })}
                  {isSecExpanded &&
                    (() => {
                      const originalIndexByRow = new Map(
                        secGroup.rows.map((r, i) => [r, i] as const),
                      )
                      return explanationSimilarity
                        .reorder(secGroup.rows)
                        .map((row) =>
                          renderRawRow(
                            row,
                            originalIndexByRow.get(row) ?? 0,
                            "pl-14",
                          ),
                        )
                    })()}
                </React.Fragment>
              )
            })
          : isExpanded
            ? (() => {
                const originalIndexByRow = new Map(
                  group.rows.map((r, i) => [r, i] as const),
                )
                return explanationSimilarity
                  .reorder(group.rows)
                  .map((row) =>
                    renderRawRow(
                      row,
                      originalIndexByRow.get(row) ?? 0,
                      "pl-10",
                    ),
                  )
              })()
            : null}
      </React.Fragment>
    )
  }

  const selectedCount = selectionEnabled
    ? (selectedSimulationOutputIds?.length ?? 0)
    : 0
  const allSimulationOutputIds = runRows
    .map((row) => row.simulation_output_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0)
  const handleClearSelection = () => {
    if (onSelectedSimulationOutputIdsChange) {
      onSelectedSimulationOutputIdsChange([])
    }
  }
  const handleToggleSelectMode = () => {
    if (isSelectMode && onSelectedSimulationOutputIdsChange) {
      onSelectedSimulationOutputIdsChange([])
    }
    setIsSelectMode((prev) => !prev)
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-none border-b border-coval-border px-6 py-3 text-sm text-coval-text-muted flex items-center justify-between gap-3">
        <div>
          Grouped by{" "}
          {GROUPED_BY_COPY[compareBy.dimension] ??
            compareBy.metadataKey ??
            "metadata"}
          {secondaryCompareBy
            ? `, then ${
                GROUPED_BY_COPY[secondaryCompareBy.dimension] ??
                secondaryCompareBy.metadataKey ??
                "metadata"
              }`
            : null}
        </div>
        <div className="flex items-center gap-3">
          {selectionAvailable && (
            <Button
              variant="secondary"
              icon
              tooltip="Scope / Rerun"
              active={isSelectMode}
              aria-pressed={isSelectMode}
              onClick={handleToggleSelectMode}
            >
              <CheckSquare />
            </Button>
          )}
          {explanationSimilarity.isActive ? (
            <SimilarityThresholdSlider
              value={explanationSimilarity.similarityThreshold}
              onChange={explanationSimilarity.setSimilarityThreshold}
            />
          ) : null}
          <span className="text-xs text-coval-text-muted mr-1">Mode</span>
          {GROUPED_RESULTS_MODE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-label={`Set mode to ${option.label}`}
              className={`px-2 py-1 text-xs rounded ${
                mode === option.value
                  ? "bg-surface-1 text-coval-text"
                  : "bg-transparent text-coval-text-muted hover:bg-surface-1"
              }`}
              onClick={() => setMode(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {selectionEnabled && selectedCount > 0 && (
        <div className="flex-none border-b border-coval-border px-6 py-2 flex items-center gap-2 flex-wrap">
          <Button variant="secondary" onClick={handleClearSelection}>
            <X />
            Clear
          </Button>
          {selectionToolbarLeading}
          {rerunMetricRecordsById && (
            <AddMetricsToRunButton
              metricRecordsById={rerunMetricRecordsById}
              simulationOutputIds={allSimulationOutputIds}
              selectedSimulationOutputIds={selectedSimulationOutputIds}
              forcedMetricIds={metricIdFilter ? [metricIdFilter] : undefined}
            />
          )}
          <div className="flex-1" />
          <span className="text-sm text-coval-text-muted">
            {selectedCount} selected
          </span>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-surface-0 z-10 border-b border-coval-border">
            <tr>
              {selectionEnabled && (
                <th
                  className="bg-surface-0 border-b border-coval-border py-2 px-3 w-10"
                  aria-label="Selection"
                />
              )}
              <th className="bg-surface-0 text-coval-text-muted text-xs font-semibold border-b border-coval-border text-left py-2 px-3 pl-6 w-64">
                Group
              </th>
              {metricColumns.map((col) => (
                <th
                  key={col.key}
                  className="bg-surface-0 text-coval-text-muted text-xs font-semibold border-b border-coval-border text-center py-2 px-3 min-w-[100px]"
                >
                  {col.name}
                </th>
              ))}
              {explanationColumns.map((col) => (
                <th
                  key={col.key}
                  className="bg-surface-0 text-coval-text-muted text-xs font-semibold border-b border-coval-border text-left py-2 px-3"
                >
                  {col.title}
                </th>
              ))}
              <th
                className="bg-surface-0 text-coval-text-muted text-xs font-semibold border-b border-coval-border text-left py-2 px-3"
                style={{ width: "200px", minWidth: "200px" }}
              >
                Test Case
              </th>
            </tr>
          </thead>
          <tbody>
            {groupEntries.map(([groupKey, group]) =>
              renderPrimaryGroup({ groupKey, group }),
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
